import Hammer from "hammerjs";
import * as cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import cornerstoneMath from "cornerstone-math";
import ProbeTool from "../../src/Probe";
import IOLProbeTool from "../../src/IOLProbeTool";
import PupilProbeTool from "../../src/PupilProbeTool";
import CorneaProbeTool from "../../src/CorneaProbeTool";
import dicomParser from "dicom-parser";
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import * as cornerstoneWebImageLoader from "cornerstone-web-image-loader";
import EllipseGradingModule from "./ellipse_grading_module";
import {
  PanTool,
  WwwcTool,
  ZoomMouseWheelTool,
  LengthTool,
  ArrowAnnotateTool,
  AngleTool,
  RectangleRoiTool,
  FreehandRoiTool,
  EraserTool,
  EllipticalRoiTool,
} from "cornerstone-tools";

// cornerstone_grading_module.js
export default class CornerstoneGradingModule {
  constructor({
    formSaveUrl,
    formUserId,
    formStackId,
    notifications,
    annotation = {},
    ellipseData = {},
    element = null,
    imageTag = null,
    url = "",
    probCount = null,
    method = null,
    gradingForm = null,
    releaseEye = null,
    IOLDiameter = null,
    minPoints = null,
    iolGradingService = null,
    controller = null,
    gradingExistUrl = null,
  }) {
    this.saveUrl = formSaveUrl;
    this.user_id = formUserId;
    this.stack_id = formStackId;
    this.annotation = annotation;
    this.ellipseData = ellipseData;
    this.viewerElement = element;
    this.imageTag = imageTag;
    this.url = url;
    this.probCount = probCount;
    this.method = method;
    this.gradingForm = gradingForm;
    this.releaseEye = releaseEye;
    this.iolDiameter = IOLDiameter;
    this.minPoints = minPoints;
    this.iolGradingApi = iolGradingService;
    this.controller = controller;
    this.gradingExistUrl = gradingExistUrl;
    this.notifications = notifications;
    this.isSaving = false;
    this.imageTagList = [
      { label: "Study Date", hex: "x00080020" },
      { label: "Study Time", hex: "x00080030" },
      { label: "Operators Name", hex: "x00081070" },
      { label: "Manufacturer's Model Name", hex: "x00081090" },
      { label: "Patient ID", hex: "x00100020" },
      { label: "Device Serial Number", hex: "x00181000" },
      { label: "Software Version(s)", hex: "x00181020" },
      { label: "Image Laterality", hex: "x00200062" },
    ];
    this.annotationIsLoadedOnce = {};
    this.images = [];
    this.currentImage = 0;
    this.selectedTool = "";
    this.LEFT_CLICK = 1;
    this.MIDDLE_CLICK = 4;
    this.initializeCornerstone();
    this.scaleFactor = 2;
    this.visibilityStates = {
      iol: "both",
      pupil: "both",
      cornea: "both",
    };
    this.activeTypes = {
      iol: true,
      pupil: true,
      cornea: true,
    };
    this.ellipseGrading = new EllipseGradingModule({
      controller: this.controller,
      iolGradingApi: this.iolGradingApi,
      saveUrl: this.saveUrl,
      method: this.method,
      minPoints: this.minPoints,
      iolDiameter: this.iolDiameter,
      ellipseData: this.ellipseData,
      cornerstone: cornerstone, // Pass cornerstone dependency
      visibilityStates: this.visibilityStates,
      activeTypes: this.activeTypes,
    });
  }

  async initializeCornerstone() {
    // Set external dependencies for cornerstone and tools
    cornerstoneWebImageLoader.external.cornerstone = cornerstone;
    cornerstoneTools.external.cornerstone = cornerstone;
    cornerstoneTools.external.Hammer = Hammer;
    cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    cornerstoneWADOImageLoader.configure({
      beforeSend: function (xhr) {},
      useWebWorkers: true,
    });

    // Suppress colorLUT warning from cornerstoneTools
    if (!window._cornerstoneWarnFiltered) {
      const originalWarn = console.warn;
      console.warn = function (...args) {
        const message = args.join(" ");
        if (
          message.includes("colorLUT") &&
          message.includes("segmentsPerLabelmap")
        ) {
          return; // Suppress this specific warning
        }
        originalWarn.apply(console, args);
      };
      window._cornerstoneWarnFiltered = true;
      window._originalConsoleWarn = originalWarn;
    }

    // Initialize cornerstoneTools with your preferred configuration
    cornerstoneTools.init({
      mouseEnabled: true,
      touchEnabled: true,
      globalToolSyncEnabled: true,
      showSVGCursors: true,
    });

    // Configure the image loader error handler
    cornerstoneTools.loadHandlerManager.setErrorLoadingHandler(
      (element, imageId, error) => {
        console.error(`Error loading image: ${imageId}`, error);
      },
    );

    // Enable cornerstone on the element (viewerElement in this case)
    cornerstone.enable(this.viewerElement);

    // Load the initial image or perform additional setup
    if (this.url) {
      await this.imageLoader(this.url);
    }

    // Attach event listeners to the cornerstone element
    this.addEventListeners();
    // Add ellipse renderer after viewer is initialized
    if (this.ellipseGrading?.addEllipseRenderer) {
      this.ellipseGrading.addEllipseRenderer();
    }

    // To apply a short cuts from keyboard.
    window.addEventListener("keydown", function (event) {
      if (!event.shiftKey) return;

      const viewer = document.querySelector(
        '[data-cornerstone-grade-target="viewer"]',
      );
      if (!viewer) return;

      // Try to get the actual enabled element (usually canvas parent)
      const element = viewer.querySelector("canvas")?.parentElement;
      if (!element || !cornerstone.getEnabledElement) return;

      let enabledElement;
      try {
        enabledElement = cornerstone.getEnabledElement(element);
      } catch (err) {
        console.warn("Cornerstone element is not enabled yet:", err);
        return;
      }

      let dx = 0,
        dy = 0;
      const panAmount = 10; // pixels to pan

      switch (event.key) {
        case "ArrowUp":
          dy = -panAmount;
          break;
        case "ArrowDown":
          dy = panAmount;
          break;
        case "ArrowLeft":
          dx = -panAmount;
          break;
        case "ArrowRight":
          dx = panAmount;
          break;
        default:
          return;
      }

      const viewport = cornerstone.getViewport(element);
      viewport.translation.x += dx;
      viewport.translation.y += dy;
      cornerstone.setViewport(element, viewport);

      event.preventDefault(); // Prevent default scroll behavior
    });

    document.addEventListener("turbo:load", () => {
      if (window.Stimulus) {
        window.Stimulus.load(window.Stimulus.definitions);
      }
    });
  }

  async imageLoader(imageUrl) {
    // This method will load the image. You can customize it according to your project.
    // Example for loading an image using the WADO image loader.
    if (!imageUrl) return;

    const imageId = `wadouri:${imageUrl}`;
    cornerstone
      .loadAndCacheImage(imageId)
      .then((image) => {
        // Display the image in the viewer
        cornerstone.displayImage(this.viewerElement, image);
      })
      .catch((error) => {
        console.error(`Error loading image from ${imageUrl}`, error);
      });
  }

  addEventListeners() {
    // Attach cornerstone-specific event listeners
    this.viewerElement.addEventListener("cornerstonenewimage", (e) => {
      // Reset viewer and adjust displayedArea on new image
      cornerstone.reset(this.viewerElement);
      e.detail.viewport.displayedArea.brhc.x = e.detail.image.width;
      e.detail.viewport.displayedArea.brhc.y = e.detail.image.height;
    });

    // Attach other cornerstoneTools event listeners if needed
    const toolEvents = cornerstoneTools.EVENTS;
    if (cornerstoneTools && cornerstoneTools.eventTarget) {
      cornerstoneTools.eventTarget.addEventListener(
        toolEvents.TOOL_ACTIVATED,
        (event) => {
          console.log("Tool activated:", event.detail.toolName);
        },
      );

      cornerstoneTools.eventTarget.addEventListener(
        toolEvents.TOOL_DEACTIVATED,
        (event) => {
          console.log("Tool deactivated:", event.detail.toolName);
        },
      );

      cornerstoneTools.eventTarget.addEventListener(
        toolEvents.STACK_CHANGED,
        (event) => {
          console.log("Stack changed:", event.detail.stack);
        },
      );
    }
  }

  imageLoader(imageUrl) {
    imageUrl = document.location.origin + "/" + imageUrl;
    this.imageTag.innerHTML = "";
    if (imageUrl) {
      let imageIds = [];
      let imageIdRoot = "wadouri:" + imageUrl;
      let stack = {};

      if (imageUrl.includes(".dcm")) {
        cornerstoneWADOImageLoader.wadouri.dataSetCacheManager
          .load(imageUrl, cornerstoneWADOImageLoader.internal.xhrRequest)
          .then((dataSet) => {
            let numFrames = dataSet.intString("x00280008");

            this.imageTagList.forEach((t) => {
              if (dataSet.intString(t.hex)) {
                this.imageTag.innerHTML += `<div>${
                  t.label
                } : ${dataSet.intString(t.hex)}</div>`;
              }
            });

            if (!numFrames) {
              imageIds.push(imageIdRoot);
            } else {
              for (let i = 0; i < numFrames; i++) {
                imageIds.push(imageIdRoot + "?frame=" + i);
              }
            }

            stack = {
              currentImageIdIndex: 0,
              imageIds: imageIds,
            };

            if (imageIds.length > 2) {
              cornerstone.loadAndCacheImage(imageIds[0]).then(
                (image) => {
                  cornerstone.displayImage(this.viewerElement, image);
                  cornerstoneTools.addStackStateManager(this.viewerElement, [
                    "stack",
                  ]);
                  cornerstoneTools.addToolState(
                    this.viewerElement,
                    "stack",
                    stack,
                  );

                  stack.imageIds.forEach((imageId) => {
                    this.loadExistingAnnotationsForImage(imageId);
                  });

                  this.addCornerstoneToolsAndAnnotations();
                },
                function (err) {
                  console.log("loadAndCacheImage error", err);
                },
              );
            } else {
              cornerstone.loadAndCacheImage(imageUrl).then(
                (image) => {
                  cornerstone.displayImage(this.viewerElement, image);
                  this.addCornerstoneToolsAndAnnotations();
                  this.loadExistingAnnotationsForImage(imageUrl);
                },
                function (err) {
                  console.log("loadImage error", err);
                },
              );
            }
          });
      } else {
        cornerstone.loadAndCacheImage(imageUrl).then((image) => {
          cornerstone.displayImage(this.viewerElement, image);
          this.addCornerstoneToolsAndAnnotations();
          this.loadExistingAnnotationsForImage(imageUrl);
        });
      }
    }
  }

  addCornerstoneToolsAndAnnotations(imageUrl) {
    cornerstoneTools.addTool(PanTool);
    cornerstoneTools.setToolActive("Pan", {
      mouseButtonMask: this.MIDDLE_CLICK,
    });

    // Activate WWWc tool on Left Click Drag
    cornerstoneTools.addTool(WwwcTool);
    cornerstoneTools.setToolActive("Wwwc", {
      mouseButtonMask: this.LEFT_CLICK,
    });

    // Activate Zoom Mouse Wheel Tool
    cornerstoneTools.addTool(ZoomMouseWheelTool, {
      configuration: {
        invert: false,
        preventZoomOutsideImage: false,
        minScale: 0.01,
        maxScale: 20.0,
      },
    });
    cornerstoneTools.setToolActive("ZoomMouseWheel", {});

    // Add other tools
    const tools = [
      LengthTool,
      ArrowAnnotateTool,
      AngleTool,
      RectangleRoiTool,
      FreehandRoiTool,
      EraserTool,
      ProbeTool,
      IOLProbeTool,
      PupilProbeTool,
      CorneaProbeTool,
      EllipticalRoiTool,
    ];

    // Ensure tools are defined before adding them
    tools.forEach((tool) => {
      if (tool) {
        try {
          cornerstoneTools.addTool(tool);
        } catch (error) {
          console.error(
            `Failed to add tool: ${tool.name || "Unnamed Tool"}`,
            error,
          );
        }
      } else {
        console.warn("Attempted to add an undefined tool");
      }
    });
    // Register cornerstone tools events
    this.viewerElement.addEventListener(
      "cornerstonetoolsmeasurementcompleted",
      (e) => {
        // console.log(`------${e.detail.toolName}-----`);
        // console.table(e.detail.measurementData);
      },
    );

    let jsonData = this.probCount; // Initialize count variable for circles
    let probeAnnotation = JSON.parse(jsonData);
    let circleCount = probeAnnotation.data ? probeAnnotation.data.length : 0;

    this.viewerElement.addEventListener(
      "cornerstonetoolsmeasurementadded",
      (e) => {
        if (e.detail.toolName !== "Probe") {
          return;
        }
        circleCount++;
        this.appendCountInTheForm(circleCount); // This should call the method
      },
    );

    this.viewerElement.addEventListener(
      "cornerstonetoolsmeasurementremoved",
      (e) => {
        if (e.detail.toolName !== "Probe") {
          return;
        }
        circleCount--;
        this.appendCountInTheForm(circleCount);
      },
    );

    this.refreshTools();
  }

  appendCountInTheForm(counts) {
    this.gradingForm.components.forEach((component) => {
      if (component.components) {
        component.components.forEach((question) => {
          if (question.key === "marks_count_" + this.releaseEye) {
            const countComponent = document.querySelector(
              `.formio-component-${question.key}`,
            );
            if (countComponent) {
              const inputElement = countComponent.querySelector("input");
              if (inputElement) {
                // Set the value programmatically
                inputElement.value = counts;
                // Trigger an input event to notify the framework of the value change
                const event = new Event("input", { bubbles: true });
                inputElement.dispatchEvent(event);
              }
            }
          }
        });
      }
    });
  }

  loadExistingAnnotationsForImage(url) {
    // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
    const getFilename = (url) => {
      const parts = url.split("/");
      return parts[parts.length - 1]; // Get last part (filename)
    };

    const currentFilename = getFilename(url);

    // Try exact match first
    if (this.annotation[url] && !this.annotationIsLoadedOnce[url]) {
      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        url,
        this.annotation[url],
      );
      this.annotationIsLoadedOnce[url] = true;
      this.refreshTools();
    } else {
      // Try matching by filename since ActiveStorage tokens change
      for (const annotationUrl in this.annotation) {
        const annotationFilename = getFilename(annotationUrl);

        if (
          annotationFilename === currentFilename &&
          !this.annotationIsLoadedOnce[url]
        ) {
          cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
            url,
            this.annotation[annotationUrl],
          );
          this.annotationIsLoadedOnce[url] = true;
          this.refreshTools();
          break;
        }
      }
    }
  }

  refreshTools() {
    const tools = [
      "Probe",
      "IOLProbe",
      "PupilProbe",
      "CorneaProbe",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Length",
      "Pan",
    ];
    tools.forEach((tool) => {
      cornerstoneTools.setToolActive(tool, { mouseButtonMask: 1 });
    });
  }

  readImages() {
    const images = document.querySelectorAll(".image");
    images.forEach((i) => {
      this.images.push({
        id: i.getAttribute("data-id"),
        url: i.getAttribute("data-url"),
        elem: i,
      });
    });
  }

  loadImage(url, id) {
    url = document.location.origin + url;
    cornerstone.loadAndCacheImage(url).then((image) => {
      this.loadExistingAnnotationsForImage(url);
      cornerstone.displayImage(this.viewerElement, image);
      cornerstone.enable(this.viewerElement);
    });
  }

  nextImage(e) {
    console.log(`current index: ${this.currentImage}`);
    const len = this.images.length;
    this.currentImage = Math.abs((this.currentImage + 1) % len);
    console.log(` after: ${this.currentImage}`);
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  previousImage(e) {
    console.log(`current index: ${this.currentImage}`);
    const len = this.images.length;
    this.currentImage = Math.abs(this.currentImage + len - 1) % len;
    console.log(` after: ${this.currentImage}`);
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  toggleTags(e) {
    this.imageTag.classList.toggle("hidden");
    e.preventDefault();
  }

  getCircleCount() {
    return cornerstoneTools.getToolState(this.viewerElement, "Probe")
      ? cornerstoneTools.getToolState(this.viewerElement, "Probe").data.length
      : 0;
  }

  labelTool(e) {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Probe",
      "IOLProbeTool",
      "PupilProbeTool",
      "CorneaProbeTool",
    ];

    tools.forEach((tool) => {
      cornerstoneTools.setToolEnabled(tool, {
        mouseButtonMask: this.LEFT_CLICK,
      });
    });

    this.viewerElement.addEventListener("cornerstonetoolsmousedown", (e) => {
      const coord = e.detail.currentPoints.canvas;
      const mouseBtn = e.detail.buttons;
      // if its a right click
      if (mouseBtn === 2) {
        tools.forEach((tool) => {
          const state = cornerstoneTools.getToolState(this.viewerElement, tool);
          if (state) {
            state.data.forEach((d) => {
              const isNear = cornerstoneTools
                .getToolForElement(this.viewerElement, tool)
                .__proto__.pointNearTool(this.viewerElement, d, coord);
              if (isNear)
                console.log(`${tool} with ${d.uuid} near : ${isNear}`);
              cornerstoneTools.setToolActive(tool, {
                mouseButtonMask: this.LEFT_CLICK,
              });
              // when near is detected see if we can have the annotation highlited green
            });
          }
        });
      }
    });
    e.preventDefault();
  }

  activateTool(toolName) {
    // Activate the tool using cornerstoneTools
    cornerstoneTools.setToolActive(toolName, {
      mouseButtonMask: this.LEFT_CLICK,
    });
  }

  setSelectedTool(toolName) {
    this.selectedTool = toolName;
  }

  async submitGrading(e) {
    e.preventDefault();
    this.notifications.showLoader();
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Probe",
      "IOLProbe",
      "PupilProbe",
      "CorneaProbe",
    ];

    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
    const button = document.getElementsByClassName("iolDecentration")[0];
    const isChecked = button?.getAttribute("aria-checked") === "true";
    const imageUrl = document.location.origin + "/" + this.controller.url;
    try {
      let ellipseData = null;

      if (isChecked) {
        ellipseData = await this.ellipseGrading.designEllipse(
          annotations,
          imageUrl,
        );
        await this.appendValues(ellipseData);
        this.playWithEllipseDataBottons(annotations, ellipseData);
      }

      // Update this.annotation with the latest annotations
      this.annotation = this.annotation || {};
      this.annotation[imageUrl] = annotations[imageUrl] || {};

      // Add timestamp to track when this image was annotated
      if (this.annotation[imageUrl]) {
        this.annotation[imageUrl]._timestamp = Date.now();
      }

      await this.controller.formSaveDraft(e, true); // Skip validation for IOL processing
      await this.saveGrading(annotations, ellipseData);
      this.notifications.hideLoader();
    } catch (error) {
      this.notifications.hideLoader();
      console.error("Grading submission failed:", error.message);
      throw error; // Re-throw to allow caller to handle if needed
    }
  }

  async appendValues(ellipseData) {
    if (
      !ellipseData ||
      !this.controller ||
      !this.controller.url ||
      !ellipseData[this.controller.url]
    ) {
      console.warn("Missing required data in ellipseData");
      return;
    }

    const dataSource = ellipseData[this.controller.url][0];

    const notifyValidation = (component, value) => {
      const module = this.controller?.previousVisitValidationModule;
      if (module) {
        module.afterProgrammaticValueSet(component, value);
      }
    };

    // Loop through all components in the grading form
    this.gradingForm.everyComponent((component) => {
      const dataApiKey = component.component?.attributes?.["data-api"];
      const shouldRound =
        component.component?.attributes?.["data-round"] === "true";

      if (!dataApiKey) return;

      if (dataSource && dataSource[dataApiKey] !== undefined) {
        let valueToSet = dataSource[dataApiKey];

        // Format IOL_Pupil_Distance to 2 decimal places
        if (
          dataApiKey === "IOL_Pupil_Distance" &&
          typeof valueToSet === "number"
        ) {
          // Round to 2 decimal places
          const roundedValue = Math.round(valueToSet * 100) / 100;
          valueToSet = roundedValue;

          // Try to format the display to show 2 decimal places
          try {
            component.setValue(roundedValue);
            // If it's a textfield, set the formatted string
            if (component.component.type === "textfield") {
              component.setValue(roundedValue.toFixed(2));
              notifyValidation(component, roundedValue.toFixed(2));
            } else if (component.refs?.input && component.refs.input[0]) {
              // For number fields, try to update the display value
              const input = component.refs.input[0];
              if (input && input.type === "text") {
                input.value = roundedValue.toFixed(2);
              }
              notifyValidation(component, roundedValue);
            } else {
              notifyValidation(component, roundedValue);
            }
          } catch (e) {
            console.warn(`Failed to format value for ${dataApiKey}:`, e);
          }
          return; // Skip the normal setValue below
        }
        // Round the value if data-round is true and it's a number
        else if (shouldRound && typeof valueToSet === "number") {
          valueToSet = Math.round(valueToSet); // or Math.floor(valueToSet)
        }

        try {
          component.setValue(valueToSet);
          notifyValidation(component, valueToSet);
        } catch (e) {
          console.warn(`Failed to set value for ${dataApiKey}:`, e);
        }
      } else {
        console.warn(`Data for ${dataApiKey} not found in ellipseData`);
      }
    });
  }

  playWithEllipseDataBottons(annotations, ellipseData = null) {
    const elementsWithData = document.querySelectorAll(
      "[data-cornerstone-grade-ellipse-data]",
    );
    if (elementsWithData.length > 0) {
      elementsWithData.forEach((element) => {
        element.setAttribute(
          "data-cornerstone-grade-annotation",
          JSON.stringify(annotations),
        );
        element.setAttribute(
          "data-cornerstone-grade-ellipse-data",
          ellipseData ? JSON.stringify(ellipseData) : "{}",
        );
      });
    }

    const ellipseButtons = document.querySelectorAll(".ellipse-data-button");
    ellipseButtons.forEach((button) => {
      if (!ellipseData || JSON.stringify(ellipseData) === "{}") {
        button.classList.add("hidden");
      } else {
        button.classList.remove("hidden");
      }
    });

    // Force Stimulus to reconnect controllers
    if (window.Stimulus) {
      window.Stimulus.load(window.Stimulus.definitions);
    }
  }

  async saveGrading(annotations, ellipseData = null) {
    if (this.isSaving) return; // prevent second call
    this.isSaving = true;

    const gradingPayload = {
      comment: "Update",
      annotations: annotations,
      user_id: this.user_id,
      stack_id: this.stack_id,
    };

    if (ellipseData) {
      gradingPayload.ellipse_data = ellipseData;
    }

    try {
      let url = this.saveUrl.replace(/\/$/, ""); // strip trailing "/"
      let method = this.method || "POST";

      const existing = await this.checkIfGradingExists();
      if (existing.exists) {
        const gradingId = existing.id;

        // Check if URL already contains '/gradings'
        if (url.includes("/gradings")) {
          // URL already has '/gradings' somewhere, so extract the base URL before '/gradings'
          url = url.split("/gradings")[0];
        } else {
          // Just clean up any JSON extensions if present
          url = url.replace(/\.json$/, "");
        }

        // Now append the correct path
        url = `${url}/gradings/${gradingId}.json`;
        method = "PATCH";
      }
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        method: method,
        body: JSON.stringify({ grading: gradingPayload }),
      });

      return response;
    } finally {
      this.isSaving = false;
    }
  }

  async checkIfGradingExists() {
    const response = await fetch(this.gradingExistUrl);
    return await response.json(); // { exists: true, id: 7 } or { exists: false }
  }

  async defaultSaveAnnotation(e) {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
    ];
    // const annotations = {};

    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();

    await this.saveGrading({ ...annotations, ...this.annotation });
  }

  calculateAngle(apiUrl, apiKey) {
    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
    let annotationsData = annotations
      ? annotations[Object.keys(annotations)[0]]
      : null;
    if (annotationsData) {
      let lengthData = annotationsData.Length;
      if (lengthData && lengthData.data.length === 2) {
        // Extracting Length data from annotationsData
        const annotationsData = lengthData.data.map((annotation) => ({
          visible: annotation.visible,
          active: annotation.active,
          invalidated: annotation.invalidated,
          handles: {
            start: annotation.handles.start,
            end: annotation.handles.end,
            textBox: annotation.handles.textBox,
          },
          uuid: annotation.uuid,
          length: annotation.length,
          unit: annotation.unit,
        }));
        fetch(apiUrl, {
          headers: {
            "X-API-KEY": apiKey,
            "Content-Type": "application/json; charset=utf-8",
          },
          method: "POST",
          body: JSON.stringify({
            Length: {
              data: annotationsData,
            },
            image_url: this.url,
            user_id: this.user_id,
            stack_id: this.stack_id,
          }),
        })
          .then((response) => {
            response.status == 200
              ? this.notifications.success("Calculated Successfully!")
              : this.notifications.error("Not Done!");
            return response.json();
          })
          .then((data) => {
            document.querySelector('input[name="data[angle]"]').value =
              data.data.lens_rotation_degrees;
            document.querySelector('input[name="data[direction]"]').value =
              data.data.direction;
            this.gradingForm.submission.data.angle =
              "angle" in this.gradingForm.submission.data
                ? data.data.lens_rotation_degrees
                : data.data.lens_rotation_degrees;
            this.gradingForm.submission.data.direction =
              "direction" in this.gradingForm.submission.data
                ? data.data.direction
                : data.data.direction;
          });
      } else {
        alert("Two lines required to calculate angle");
      }
    } else {
      alert("Draw two lines through Length Tool to Calculate Angle");
    }
  }

  toggleEllipseData(e) {
    e.preventDefault();

    const type = e.currentTarget.dataset.type.toLowerCase();
    const toolMap = {
      iol: "IOLProbe",
      pupil: "PupilProbe",
      cornea: "CorneaProbe",
    };
    const toolName = toolMap[type];
    if (!toolName) return;

    const imageUrl = document.location.origin + "/" + this.controller.url;
    if (!this.annotation[imageUrl]) return;

    if (!this.annotationToggleStates) {
      this.annotationToggleStates = { iol: 0, pupil: 0, cornea: 0 };
    }

    this.annotationToggleStates[type] =
      (this.annotationToggleStates[type] + 1) % 4;
    const currentState = this.annotationToggleStates[type];

    const toolData = this.annotation[imageUrl][toolName];
    if (toolData && toolData.data) {
      if (currentState === 0) {
        toolData.data.forEach((a) => (a.visible = true));
        this.ellipseGrading.setVisibility(type, true);
      } else if (currentState === 1) {
        toolData.data.forEach((a) => (a.visible = false));
        this.ellipseGrading.setVisibility(type, true);
      } else if (currentState === 2) {
        toolData.data.forEach((a) => (a.visible = true));
        this.ellipseGrading.setVisibility(type, false);
      } else if (currentState === 3) {
        toolData.data.forEach((a) => (a.visible = false));
        this.ellipseGrading.setVisibility(type, false);
      }

      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        imageUrl,
        this.annotation[imageUrl],
      );

      this.refreshTools();

      const button = e.currentTarget;
      button.querySelector("span").textContent = this.getButtonLabel(
        type,
        currentState,
      );
    } else {
      console.error("No toolData found for toolName:", toolName);
    }
  }

  // Helper method to generate button labels
  getButtonLabel(type, state) {
    const typeLabels = {
      iol: "IOL",
      pupil: "PUPIL",
      cornea: "CORNEA",
    };

    const stateLabels = {
      0: "", // All visible
      1: "(N/P)", // Annotation hidden
      2: "(N/C)", // Ellipse hidden
      3: "(OFF)", // Both hidden
    };

    return `${typeLabels[type]} ${stateLabels[state]}`.trim();
  }
}
